import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Box, Collapse } from '@televet/kibble-ui/build/chakra';

interface CollapsableTextProps {
  text: string;
  maxLines?: number;
}

export const CollapsableText = ({ text, maxLines = 3 }: CollapsableTextProps): JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [needsCollapse, setNeedsCollapse] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      const maxHeight = maxLines * 21; // Assuming each line is approximately 21px high
      if (textRef?.current?.scrollHeight > maxHeight) {
        setNeedsCollapse(true);
      }
    }
  }, [text, maxLines]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((current) => !current);
  }, []);

  return (
    <Box alignItems="center" mb={1}>
      <Collapse startingHeight={Math.min(textRef?.current?.scrollHeight ?? 21, maxLines * 21)} in={!isCollapsed}>
        <Box ref={textRef}>
          <Text as="p" variant="subtle" size="sm" noOfLines={3}>
            {text}
          </Text>
        </Box>
      </Collapse>
      {needsCollapse && (
        <Button
          size="sm"
          onClick={toggleCollapse}
          mt={1}
          variant="ghostNeutral"
          p={0}
          h="fit-content"
          iconName={isCollapsed ? 'chevronDown' : 'chevronUp'}
          iconPosition="right"
        >
          {isCollapsed ? 'Expand Note' : 'Collapse Note'}
        </Button>
      )}
    </Box>
  );
};
